import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { BodyText, extraSmall } from '@unmind/design-system';
import { Sparkles, ChevronDown, Nova, Dots } from 'icons';

export const MIN_HEIGHT_CHAT_INPUT = rem(68);

// Chatbot styles

export const ChatbotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: ${rem(88)};
  max-width: ${rem(800)};
  min-height: 100%;
  margin: 0 auto;
`;

export const ChatbotMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  gap: ${rem(24)};
  padding-bottom: ${rem(24)};

  ${extraSmall(css`
    padding-bottom: ${rem(130)};
  `)}
`;

export const LoadingMoreWrapper = styled.div`
  align-self: center;
  padding: ${rem(16)} 0 ${rem(32)} 0;
`;

export const LoadingMoreErrorWrapper = styled.div`
  margin-bottom: 16px;
`;

export const FeatureUnavailableErrorWrapper = styled.div`
  max-width: 450px;
  margin: auto;
  margin-bottom: 16px;
`;

export const ChatbotFooter = styled.div`
  position: sticky;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${rem(24)};
  width: 100%;
  bottom: 0;
  right: 0;
  left: 0;
`;

export const ChatbotFooterContent = styled.div<{ $isMSTeams: boolean }>`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.primary};

  padding: ${({ $isMSTeams }) =>
    `0 ${rem(16)} ${rem($isMSTeams ? 30 : 102)} ${rem(16)}`};

  ${extraSmall(css`
    padding-bottom: ${rem(30)};
  `)}
`;

export const ChatBotFooterText = styled(BodyText).attrs(({ theme }) => ({
  sizes: [theme.typography.fontSizes.fontSize12],
  weight: theme.typography.fontWeights.regular,
  color: theme.colors.text.primary,
}))`
  margin: 0 auto;
  max-width: ${rem(800)};
  padding-top: ${rem(16)};
  text-align: center;
`;

export const ChatBotInputContainer = styled.div`
  max-width: ${rem(800)};
  margin: 0 auto;
`;

export const ScrollIconContainer = styled.div`
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 50px;

  background-color: ${({ theme }) => theme.colors.background.inverse};
  box-shadow: ${({ theme }) => `0px 0px 8px ${theme.colors.boxShadow}`};
`;

export const ScrollIcon = styled(ChevronDown).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.inverse,
  width: theme.typography.fontSizes.fontSize16,
  height: theme.typography.fontSizes.fontSize16,
}))``;

// Common Styles

export const InputBar = styled.div<{ $displayOnHomepage?: boolean }>`
  background-color: ${({ theme }) => theme.colors.background.input};
  border-radius: 12px;
  padding: ${rem(16)} ${rem(16)} ${rem(10)};
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  gap: ${rem(8)};
  min-height: ${MIN_HEIGHT_CHAT_INPUT};
  transition: box-shadow 0.2s ease-in-out;

  .text-input textarea:focus-visible {
    outline: none;
  }

  &:focus-within {
    box-shadow: ${({ theme }) => `0px 2px 8px 0px ${theme.colors.boxShadow}`};
  }
`;

export const SparklesIcon = styled(Sparkles).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.primary,
}))`
  width: ${rem(16)};
  height: ${rem(16)};
  margin-right: ${rem(10)};
`;

export const TextFieldWrapper = styled.div`
  flex: 1;
  width: 100%;
  overflow: hidden;
  margin-left: ${rem(-10)};
  min-height: ${rem(52)};

  textarea {
    padding: 0;
  }
`;

export const AssistantIcon = styled(Nova).attrs(({ theme }) => ({
  primaryColor: theme.colors.chatbot.logo.primary,
  secondaryColor: theme.colors.chatbot.logo.secondary,
  tertiaryColor: theme.colors.chatbot.logo.tertiary,
}))`
  width: ${rem(24)};
  height: ${rem(24)};
`;

export const SettingsMenuWrapper = styled.div`
  position: fixed;
  z-index: 10;
  padding: 8px 9px;
  top: 60px;
  right: 40px;

  ${extraSmall(css`
    top: 50px;
  `)}
`;

export const SettingsIcon = styled(Dots).attrs(({ theme }) => ({
  primaryColor: theme.colors.text.primary,
  width: 32,
  height: 32,
}))`
  border-radius: 100px;
  padding: ${rem(4)};

  &:hover {
    background-color: ${({ theme }) => theme.colors.background.disabled};
  }
`;

export const SettingsMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};

  a {
    font-size: inherit;
    font-weight: inherit;
    color: inherit;
  }
`;

export const ClearChatLabel = styled.span`
  color: ${({ theme }) => theme.colors.border.error};
`;
